// ============================================================================
// TAX MASTER SERVICE — app-side adapter for the relocatable tax-master module
// ============================================================================
// Implements the module's injected TaxMasterApi over this app's axios client
// against /api/v1/tax-masters (API template Tax module). Every scaffolded app
// mounts the identical API, so this adapter is copied unchanged.

import { get, post, put, del } from '../client/axiosClient';
import { taxMasterEndpoints } from '../endpoints/tax.endpoints';
import type { ApiResponse, BackendPagedResult } from '../types/common.types';
import type {
  AppendTaxMasterVersionRequest,
  CreateTaxMasterRequest,
  TaxMasterApi,
  TaxMasterPage,
  TaxMasterQuery,
  TaxMasterSummary,
  TaxMasterVersion,
} from 'components/shared/tax-master-module/taxMaster.types';

const EMPTY_PAGE: TaxMasterPage = {
  items: [], page: 1, pageSize: 25, totalCount: 0, totalPages: 0, hasNext: false, hasPrevious: false,
};

export const taxMasterService: TaxMasterApi = {
  async list(jurisdictionCode: string, query: TaxMasterQuery = {}): Promise<TaxMasterPage> {
    const res = await get<ApiResponse<BackendPagedResult<TaxMasterSummary>>>(
      taxMasterEndpoints.list(jurisdictionCode, query),
    );
    const paged = res.data.data;
    if (!paged) return EMPTY_PAGE;
    return {
      items: paged.items ?? [],
      page: paged.page,
      pageSize: paged.pageSize,
      totalCount: paged.totalCount,
      totalPages: paged.totalPages,
      hasNext: paged.hasNext,
      hasPrevious: paged.hasPrevious,
    };
  },

  async get(jurisdictionCode: string, rateCode: string): Promise<TaxMasterVersion[]> {
    const res = await get<ApiResponse<TaxMasterVersion[]>>(taxMasterEndpoints.get(jurisdictionCode, rateCode));
    return res.data.data || [];
  },

  async create(jurisdictionCode: string, request: CreateTaxMasterRequest): Promise<void> {
    await post(taxMasterEndpoints.create(jurisdictionCode), request);
  },

  async appendVersion(jurisdictionCode: string, rateCode: string, request: AppendTaxMasterVersionRequest): Promise<void> {
    await post(taxMasterEndpoints.appendVersion(jurisdictionCode, rateCode), request);
  },

  async replaceVersion(
    jurisdictionCode: string, rateCode: string, effectiveFrom: string, request: AppendTaxMasterVersionRequest,
  ): Promise<void> {
    await put(taxMasterEndpoints.version(jurisdictionCode, rateCode, effectiveFrom), request);
  },

  async deleteVersion(jurisdictionCode: string, rateCode: string, effectiveFrom: string, reason?: string): Promise<void> {
    await del(taxMasterEndpoints.version(jurisdictionCode, rateCode, effectiveFrom, reason));
  },

  async exportCsv(jurisdictionCode: string, query: TaxMasterQuery = {}): Promise<Blob> {
    const res = await get<Blob>(taxMasterEndpoints.exportCsv(jurisdictionCode, query), { responseType: 'blob' });
    return res.data;
  },
};

export default taxMasterService;
